import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { skip, Subscription } from 'rxjs';

import { PanSearchService } from '../../../services/pan/pan.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';

@Component({
  selector: 'app-pan-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent
  ],
  templateUrl: './pan-search-result.component.html',
  styleUrl: './pan-search-result.component.css',
})
export class PanSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  panData: any = null;

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;

  jobResultSubscription?: Subscription;

  get isLoadingResults(): boolean {
    const hasResults = !!this.panData;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  searchTerm: string = '';
  notes: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private panSearchService: PanSearchService,
    public utilsService: UtilsService,
  ) { }

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/pan-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId = this.selectedSearchResult.pan_search_id;

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult();
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
  }

  onBackButtonClick() {
    window.history.back();
  }

  getCompletedSearchResult() {
    this.loadingCompletedSearchResult = true;

    const originalSearchedPan = this.selectedSearchResult?.searched_pan;

    this.panSearchService.getCompletedSearchResult(this.searchId).subscribe({
      next: (response) => {
        const rawSearchResultData = response?.data;
        const completedSearchResult = rawSearchResultData?.data_list[0];

        this.selectedSearchResult = {
          ...completedSearchResult,
          searched_pan:
            completedSearchResult?.pan_data?.upstream_response?.data
              ?.pan_number || originalSearchedPan,
        };

        this.notes = rawSearchResultData?.notes || '';

        // Extract PAN data from the response
        if (completedSearchResult?.pan_data?.upstream_response?.data) {
          this.panData = completedSearchResult.pan_data.upstream_response.data;
        }

        this.loadingCompletedSearchResult = false;
        this.loadingPolledSearchResult = false;
      },
      error: (error) => {
        console.error(error);
        this.loadingCompletedSearchResult = false;
        this.loadingPolledSearchResult = false;
      },
    });
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;

    const originalSearchedPan = this.selectedSearchResult?.searched_pan;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (response) {
            if (response.done === true || response.status === 'completed') {
              this.getCompletedSearchResult();
            } else {
              this.selectedSearchResult = {
                ...this.selectedSearchResult,
                done: response.done || false,
                status: response.status || 'pending',
                searched_pan: response?.searched_pan || originalSearchedPan,
              };
            }
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  onSearchTermChange() {
    // Search functionality can be implemented if needed
    // For now, keeping it simple
  }

  onExportOptionClick(exportFormat: string) {
    this.exportSearchResult(exportFormat);
  }

  exportSearchResult(format: string) {
    this.panSearchService.exportSearchResult(this.searchId, format).subscribe({
      next: (response) => {
        this.saveFile(response?.data);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  saveFile(fileInfo: any) {
    const { fileName, mimeType, base64 } = fileInfo;

    const linkSource = `data:${mimeType};base64,${base64}`;
    const saveLink = document.createElement('a');

    saveLink.href = linkSource;
    saveLink.download = fileName;
    saveLink.click();
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.panSearchService.updatePanNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      if (this.utilsService && this.utilsService.formatDateTime) {
        return this.utilsService.formatDateTime(dateString, 'MMM d, y');
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '-';
    try {
      if (this.utilsService && this.utilsService.formatDateTime) {
        return this.utilsService.formatDateTime(dateString);
      }
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }
}
